import { useEffect, useState } from "react";
import { Button, Col, Form, Input, Row, Spin } from "antd";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import userServiceApi from "../../../../../../api/userServiceApi";
import {
  getRequest,
  postRequest,
  putRequest,
} from "../../../../../../api/http";
import { contactUSkeys } from "../../../../../../config";
import EmptyTabWarning from "../../../../../common/EmptyTabWarning";
import { usePublishedEmptyTabWarning } from "../../../../../../custom-hooks/usePublishedEmptyTabWarning";

const { TextArea } = Input;
const MAX_CHARACTERS = 150;

const GettingToUs = ({ tabKey }) => {
  const location = useLocation();
  const micrositeId = location.pathname.split("/")[1];
  const tabConfigId = localStorage.getItem("selectedTabId");
  const whichTab = contactUSkeys.gettingToUs;

  const [form] = Form.useForm();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [clearButtonDisabled, setClearButtonDisabled] = useState(true);
  const [tabApiData, setTabApiData] = useState(undefined);
  const [tabApiLoaded, setTabApiLoaded] = useState(false);

  const { show: showEmptyTabWarning, tabName: emptyTabName } =
    usePublishedEmptyTabWarning({
      tabKey: whichTab,
      data: tabApiData,
      loaded: !loading && tabApiLoaded,
    });

  const checkClearButtonState = () => {
    const description = form.getFieldValue("description") || "";
    setClearButtonDisabled(description.length === 0);
  };

  const getTabInfo = async () => {
    setLoading(true);
    try {
      const res = await getRequest(
        userServiceApi.getContactUsDetails(whichTab, micrositeId)
      );
      if (res?.status === 200 && Array.isArray(res.data)) {
        setTabApiData(res.data);
        setTabApiLoaded(true);
      }
      const item = res?.data?.[0];
      setData(item || null);
      form.setFieldsValue({
        description: item?.description || "",
      });
      setClearButtonDisabled(!(item?.description?.length > 0));
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    }
    setLoading(false);
  };

  const handleSave = async (values) => {
    try {
      const payload = {
        contactUsTypeEnum: whichTab,
        description: values.description ?? "",
        tabConfigId,
      };

      if (data?.id) {
        payload.contactUsId = data.id;
        const res = await putRequest(
          userServiceApi.updateContactUsSales(data.id),
          payload
        );
        if (res) {
          toast.success("Updated successfully");
        }
      } else {
        const res = await postRequest(
          userServiceApi.addContactUsDetails(micrositeId, tabConfigId),
          payload
        );
        if (res) {
          toast.success("Created successfully");
        }
      }
      getTabInfo();
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    }
  };

  const handleClear = () => {
    form.setFieldsValue({ description: "" });
    setClearButtonDisabled(true);
  };

  useEffect(() => {
    if (tabKey === whichTab && micrositeId) {
      getTabInfo();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabKey, micrositeId]);

  if (loading) {
    return (
      <div
        className="data-loader"
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          width: "100%",
          textAlign: "center",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Spin tip="Loading" size="large" />
      </div>
    );
  }

  if (tabKey !== whichTab) {
    return null;
  }

  return (
    <div>
      {showEmptyTabWarning && <EmptyTabWarning tabName={emptyTabName} />}
      <Row className="rowBody" gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
        <Col className="gutter-row tabContent" span={15}>
          <Form
            form={form}
            layout="horizontal"
            onFinish={handleSave}
            initialValues={{ description: "" }}
          >
            <div style={{ marginBottom: "10px" }} className="inputBody">
              <label></label>
              <div style={{ textAlign: "right" }}>
                <Button
                  className="cancelBtn"
                  type="button"
                  disabled={clearButtonDisabled}
                  onClick={handleClear}
                >
                  Clear
                </Button>
              </div>
            </div>

            <Form.Item
              className="w-100"
              label="Description"
              labelCol={{ span: 9 }}
              name="description"
              rules={[
                {
                  required: true,
                  message: "Description is required",
                },
                {
                  max: MAX_CHARACTERS,
                  message: `Description should not exceed ${MAX_CHARACTERS} characters`,
                },
              ]}
            >
              <TextArea
                placeholder="Enter getting to us description"
                maxLength={MAX_CHARACTERS}
                showCount
                style={{ whiteSpace: "pre-wrap", height: 200, minHeight: 200 }}
                onChange={checkClearButtonState}
              />
            </Form.Item>

            <Form.Item
              className="w-100"
              style={{
                display: "flex",
                justifyContent: "end",
              }}
            >
              <Button
                className="saveBtn greenBtn"
                htmlType="button"
                onClick={() => form.submit()}
              >
                Save
              </Button>
            </Form.Item>
          </Form>
        </Col>
      </Row>
    </div>
  );
};

export default GettingToUs;
